import { DEFAULTS } from '../../constants';
import { toTrimmedString } from './shared';

const WARNING_ASSET_PATTERN = /Asset \((\d+)\)/;

const extractAssetFromMessage = (message) => {
  if (!message) return { assetId: null, assetLabel: DEFAULTS.EMPTY_STRING };
  const matchedAsset = String(message).match(WARNING_ASSET_PATTERN);
  if (!matchedAsset) return { assetId: null, assetLabel: DEFAULTS.EMPTY_STRING };
  return { assetId: matchedAsset[1], assetLabel: matchedAsset[0] };
};

const toWarningEntry = (warning, sublocationNameById = {}, sublocationId = null) => {
  if (!warning) return null;

  if (typeof warning === 'string') {
    const warningMessage = warning.trim();
    if (!warningMessage) return null;
    const { assetId, assetLabel } = extractAssetFromMessage(warningMessage);
    return {
      message: warningMessage,
      assetId,
      assetLabel,
    };
  }

  const warningSublocationId = warning?.sublocationId ?? sublocationId;
  const sublocationName =
    toTrimmedString(warning?.sublocationName) || toTrimmedString(sublocationNameById[String(warningSublocationId)]);
  const warningType = toTrimmedString(warning?.type);
  const warningAssetId = warning?.assetId;
  const normalizedWarningAssetId =
    warningAssetId !== undefined && warningAssetId !== null ? String(warningAssetId).trim() : DEFAULTS.EMPTY_STRING;
  const assetName = toTrimmedString(warning?.assetName);
  const warningMessage = toTrimmedString(warning?.message);

  const sublocationLabel = sublocationName
    ? warningSublocationId
      ? `${sublocationName} (${warningSublocationId})`
      : sublocationName
    : warningSublocationId
    ? `Sublocation (${warningSublocationId})`
    : DEFAULTS.EMPTY_STRING;

  const assetLabel = normalizedWarningAssetId
    ? assetName
      ? `${assetName} (${normalizedWarningAssetId})`
      : `Asset (${normalizedWarningAssetId})`
    : assetName || DEFAULTS.EMPTY_STRING;

  const prefixParts = [sublocationLabel, warningType, assetLabel].filter(Boolean);
  const prefix = prefixParts.join(' | ');

  const constructedMessage = warningMessage && prefix ? `${prefix}: ${warningMessage}` : warningMessage || prefix;

  if (constructedMessage) {
    const fallbackAssetFromMessage = extractAssetFromMessage(constructedMessage);
    return {
      message: constructedMessage,
      assetId: normalizedWarningAssetId || fallbackAssetFromMessage.assetId,
      assetLabel: assetLabel || fallbackAssetFromMessage.assetLabel,
    };
  }

  try {
    const rawMessage = JSON.stringify(warning);
    const fallbackAssetFromMessage = extractAssetFromMessage(rawMessage);
    return {
      message: rawMessage,
      assetId: fallbackAssetFromMessage.assetId,
      assetLabel: fallbackAssetFromMessage.assetLabel,
    };
  } catch (error) {
    return null;
  }
};

export const extractWarnings = (data, { sublocationNameById = {} } = {}) => {
  if (!data) return DEFAULTS.EMPTY_ARRAY;
  const payload = data?.data ?? data;
  const normalizedWarnings =
    payload?.warnings ?? payload?.warningMessages ?? payload?.warning_messages ?? DEFAULTS.EMPTY_ARRAY;

  if (Array.isArray(normalizedWarnings)) {
    return normalizedWarnings.map((warning) => toWarningEntry(warning, sublocationNameById)).filter(Boolean);
  }

  if (normalizedWarnings && typeof normalizedWarnings === 'object') {
    return Object.entries(normalizedWarnings)
      .flatMap(([sublocationId, sublocationWarnings]) => {
        if (Array.isArray(sublocationWarnings)) {
          return sublocationWarnings.map((warning) => toWarningEntry(warning, sublocationNameById, sublocationId));
        }
        return [toWarningEntry(sublocationWarnings, sublocationNameById, sublocationId)];
      })
      .filter(Boolean);
  }

  return DEFAULTS.EMPTY_ARRAY;
};
